import type { ReactNode } from "react";
import { cn } from "@flint/ui";

/**
 * The centred content column shared by the store's single-record screens.
 *
 * New / edit / detail had drifted apart — the create form was `mx-auto
 * max-w-5xl` while detail and edit sat left-aligned at `max-w-4xl`, and every
 * page header was full-bleed regardless. Wrapping BOTH the header and the body
 * in this component is what makes the title, the action buttons and the cards
 * share one left edge, so the three screens read as one family.
 *
 * Use it for record screens, not for the full-width list tables.
 *
 * ## Two widths, because these screens want opposite things
 *
 * A **form** wants a short measure. `form` matches the wireframe's `.form-grid`
 * column (240px labels + ~760px fields); stretching it would give every label
 * and field a line longer than is comfortable to read.
 *
 * A **grid** wants width. A record screen that puts a table beside the 320px
 * rail only has `width − 320 − gap` left for it, and at `form` width that is
 * 684px. Measured on order detail: the five-column lines table rendered 650px,
 * sitting near its `min-w` floor with every column at its content minimum —
 * Product 207px, SKU 104px, Price/pack 108px. Nothing overflowed; it was simply
 * squeezed. `grid` gives the same table 900px (Product 276px, Price/pack 144px),
 * which is the measure the collections line sheet already runs at.
 *
 * The default stays `form`: most consumers here are forms, and the wrong width
 * for a form is the *opposite* mistake, not a smaller version of the same one.
 */
export type StorePageColumnVariant = "form" | "grid";

const MAX_WIDTH: Record<StorePageColumnVariant, string> = {
  form: "max-w-5xl",
  grid: "max-w-7xl",
};

export function StorePageColumn({
  variant = "form",
  className,
  children,
}: {
  variant?: StorePageColumnVariant;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn("mx-auto w-full", MAX_WIDTH[variant], className)}>{children}</div>
  );
}
